import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Star, PencilSimple, LinkedinLogo, EnvelopeSimple, Phone as PhoneIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import ContactHistoryDrawer from "@/components/ContactHistoryDrawer";
import ContactsCsvImportDialog from "@/components/ContactsCsvImportDialog";
import NudgeComposerDialog from "@/components/NudgeComposerDialog";

/*
  ContactsPanel — Iter 85.4 refactor.

  Extracted from ClientDetail.jsx (which had grown to 1130+ lines and
  shipped an undefined-identifier crash in Iter 85.3). Owns every piece
  of state and every JSX branch for the CRM Contacts tab: the roster,
  the add/edit dialog, the primary toggle, the last-contacted chip,
  the decay 'reach out' nudge, the history drawer, and the CSV import.

  The parent (ClientDetail) passes the current client + a callback to
  merge server-returned updates back into its own state. That's it.
*/

export const CONTACT_ROLES = [
  { v: "primary_poc",        label: "Primary POC" },
  { v: "executive_sponsor",  label: "Executive Sponsor" },
  { v: "day_to_day",         label: "Day-to-Day" },
  { v: "billing_admin",      label: "Billing / Admin" },
  { v: "board_governance",   label: "Board / Governance" },
  { v: "other",              label: "Other" },
];
export const CONTACT_POSTURES = [
  { v: "champion",   label: "Champion" },
  { v: "supportive", label: "Supportive" },
  { v: "neutral",    label: "Neutral" },
  { v: "skeptical",  label: "Skeptical" },
  { v: "blocker",    label: "Blocker" },
  { v: "unknown",    label: "Unknown" },
];
export const CONTACT_INFLUENCES = ["low", "medium", "high"];
export const ROLE_LABEL = Object.fromEntries(CONTACT_ROLES.map((r) => [r.v, r.label]));
export const POSTURE_LABEL = Object.fromEntries(CONTACT_POSTURES.map((p) => [p.v, p.label]));

// Posture chip tone. Semantic tokens so light + dark both look right.
export const POSTURE_TONE = {
  champion:   "bg-primary/10 text-primary border-primary/40",
  supportive: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/40",
  neutral:    "bg-secondary text-foreground/70 border-border",
  skeptical:  "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/40",
  blocker:    "bg-destructive/10 text-destructive border-destructive/40",
  unknown:    "bg-muted text-muted-foreground border-border",
};

// Relationship-decay threshold. A high-influence contact untouched
// longer than this shows a subtle amber "Reach out" nudge on the row.
export const DECAY_DAYS = 90;

export function isDecayed(contact) {
  if ((contact?.influence || "").toLowerCase() !== "high") return false;
  const iso = contact?.last_contacted_at;
  if (!iso) return true;
  try {
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 3600 * 1000));
    return days > DECAY_DAYS;
  } catch { return false; }
}

export function fmtRelative(iso) {
  if (!iso) return null;
  try {
    const dt = new Date(iso);
    const days = Math.floor((Date.now() - dt.getTime()) / (24 * 3600 * 1000));
    if (days < 0) return "future";
    if (days === 0) return "today";
    if (days === 1) return "yesterday";
    if (days < 30) return `${days}d ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}yr ago`;
  } catch { return null; }
}

const EMPTY_CONTACT = {
  name: "", title: "", role: "other", posture: "unknown",
  influence: "medium", email: "", phone: "", linkedin_url: "", notes: "",
  owner_user_id: "",
};

export default function ContactsPanel({ client, onClientUpdated, users = [] }) {
  const cid = client?.id;
  const stakeholders = client?.stakeholders || [];
  const [searchParams, setSearchParams] = useSearchParams();

  const [stakeOpen, setStakeOpen] = useState(false);
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT);
  const [editingId, setEditingId] = useState(null);
  const [historyContactId, setHistoryContactId] = useState(null);
  const [csvOpen, setCsvOpen] = useState(false);
  // Iter 85.5 · Contact Merge — dialog state. `mergeSourceId` is the row
  // the partner clicked "Merge" on; the dialog then asks which OTHER
  // contact should absorb it.
  const [mergeSourceId, setMergeSourceId] = useState(null);
  const [mergeTargetId, setMergeTargetId] = useState(null);
  // Iter 85.6 · Follow-up Nudge Composer — set to the contact whose
  // "Reach out" chip was clicked. Dialog renders when non-null.
  const [nudgeContact, setNudgeContact] = useState(null);

  const userNames = useMemo(
    () => Object.fromEntries((users || []).map((u) => [u.id, u.name])),
    [users],
  );

  // Iter 85.5 · Firm-Wide Contact Search deep-link — the GlobalSearch
  // palette navigates with ?openContact=<id>; auto-open the history
  // drawer and strip the param so it's a one-shot signal.
  useEffect(() => {
    const openId = searchParams.get("openContact");
    if (!openId) return;
    setHistoryContactId(openId);
    const next = new URLSearchParams(searchParams);
    next.delete("openContact");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const openAddContact = () => {
    setEditingId(null);
    setContactForm(EMPTY_CONTACT);
    setStakeOpen(true);
  };
  const openEditContact = (s) => {
    setEditingId(s.id);
    setContactForm({
      name: s.name || "", title: s.title || "",
      role: s.role || "other", posture: s.posture || "unknown",
      influence: s.influence || "medium",
      email: s.email || "", phone: s.phone || "",
      linkedin_url: s.linkedin_url || "", notes: s.notes || "",
      owner_user_id: s.owner_user_id || "",
    });
    setStakeOpen(true);
  };
  const saveContact = async () => {
    if (!contactForm.name.trim()) return toast.error("Name required");
    // Iter 85.5 · Contact Ownership — empty picker → null (unassigned).
    const payload = { ...contactForm, owner_user_id: contactForm.owner_user_id || null };
    try {
      const { data } = editingId
        ? await api.patch(`/clients/${cid}/contacts/${editingId}`, payload)
        : await api.post(`/clients/${cid}/contacts`, payload);
      onClientUpdated?.(data.client);
      toast.success(editingId ? "Contact updated" : "Contact added");
      setStakeOpen(false);
      setEditingId(null);
      setContactForm(EMPTY_CONTACT);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const removeContact = async (contactId) => {
    try {
      const { data } = await api.delete(`/clients/${cid}/contacts/${contactId}`);
      onClientUpdated?.(data.client);
      toast.success("Contact removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };
  const setPrimaryContact = async (contactId) => {
    try {
      const { data } = await api.post(`/clients/${cid}/contacts/${contactId}/set-primary`);
      onClientUpdated?.(data.client);
      toast.success("Primary contact set");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const doMerge = async () => {
    if (!mergeSourceId || !mergeTargetId || mergeSourceId === mergeTargetId) {
      return toast.error("Pick a different target contact");
    }
    try {
      const { data } = await api.post(
        `/clients/${cid}/contacts/${mergeSourceId}/merge`,
        { target_id: mergeTargetId },
      );
      onClientUpdated?.(data.client);
      toast.success("Contacts merged");
      setMergeSourceId(null);
      setMergeTargetId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    }
  };

  return (
    <div className="mt-6">
      <div className="border border-border bg-card" data-testid="contacts-panel">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h3 className="font-display font-bold text-sm">Contacts</h3>
            <p className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-1">
              The people around this partner organization · {stakeholders.length} on file
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCsvOpen(true)}
              className="rounded-none text-[0.625rem] font-mono uppercase tracking-widest"
              data-testid="contacts-csv-import-open"
            >
              Import CSV
            </Button>
            <Dialog
              open={stakeOpen}
              onOpenChange={(v) => { setStakeOpen(v); if (!v) { setEditingId(null); setContactForm(EMPTY_CONTACT); } }}
            >
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  onClick={openAddContact}
                  className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="add-contact-btn"
                >
                  <Plus size={12} className="mr-1" /> Add contact
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none max-w-lg" data-testid="contact-dialog">
                <DialogHeader>
                  <DialogTitle>{editingId ? "Edit contact" : "Add contact"}</DialogTitle>
                  <DialogDescription className="text-xs text-muted-foreground">
                    Capture role, posture, and influence separately so relationship context stays legible.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Name</Label>
                      <Input value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} className="rounded-none bg-secondary/40 mt-1" data-testid="contact-form-name" />
                    </div>
                    <div>
                      <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Title</Label>
                      <Input value={contactForm.title} onChange={(e) => setContactForm({ ...contactForm, title: e.target.value })} className="rounded-none bg-secondary/40 mt-1" data-testid="contact-form-title" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Role on account</Label>
                      <Select value={contactForm.role} onValueChange={(v) => setContactForm({ ...contactForm, role: v })}>
                        <SelectTrigger className="rounded-none bg-secondary/40 mt-1" data-testid="contact-form-role"><SelectValue /></SelectTrigger>
                        <SelectContent>{CONTACT_ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Posture</Label>
                      <Select value={contactForm.posture} onValueChange={(v) => setContactForm({ ...contactForm, posture: v })}>
                        <SelectTrigger className="rounded-none bg-secondary/40 mt-1" data-testid="contact-form-posture"><SelectValue /></SelectTrigger>
                        <SelectContent>{CONTACT_POSTURES.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Influence</Label>
                    <Select value={contactForm.influence} onValueChange={(v) => setContactForm({ ...contactForm, influence: v })}>
                      <SelectTrigger className="rounded-none bg-secondary/40 mt-1" data-testid="contact-form-influence"><SelectValue /></SelectTrigger>
                      <SelectContent>{CONTACT_INFLUENCES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Email</Label>
                    <Input value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} className="rounded-none bg-secondary/40 mt-1" data-testid="contact-form-email" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Phone</Label>
                      <Input value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} className="rounded-none bg-secondary/40 mt-1" data-testid="contact-form-phone" />
                    </div>
                    <div>
                      <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">LinkedIn URL</Label>
                      <Input value={contactForm.linkedin_url} onChange={(e) => setContactForm({ ...contactForm, linkedin_url: e.target.value })} className="rounded-none bg-secondary/40 mt-1" data-testid="contact-form-linkedin" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Partner notes (internal)</Label>
                    <Textarea value={contactForm.notes} onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })} rows={3} placeholder="Private intelligence about this contact…" className="rounded-none bg-secondary/40 mt-1" data-testid="contact-form-notes" />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={saveContact} className="rounded-none bg-primary text-primary-foreground" data-testid="contact-form-save">
                    {editingId ? "Save changes" : "Add contact"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        {stakeholders.length === 0 ? (
          <div className="px-5 py-12 text-center text-xs text-muted-foreground" data-testid="contacts-empty">
            No contacts captured yet — add the first person around this account.
          </div>
        ) : (
          <ul className="divide-y divide-border" data-testid="contacts-list">
            {stakeholders.map((s) => {
              const rowId = s.id || `${s.email || s.name}`;
              const postureCls = POSTURE_TONE[s.posture] || POSTURE_TONE.unknown;
              return (
                <li key={rowId} className="px-5 py-4 hover:bg-secondary/30 group" data-testid={`contact-row-${rowId}`}>
                  <div className="flex items-start gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => setPrimaryContact(rowId)}
                          className={`shrink-0 ${s.is_primary ? "text-primary" : "text-muted-foreground/40 hover:text-primary/70"} transition-colors`}
                          title={s.is_primary ? "Primary POC" : "Set as primary POC"}
                          data-testid={`contact-primary-toggle-${rowId}`}
                          aria-label={s.is_primary ? "Primary contact" : "Set as primary"}
                        >
                          <Star size={14} weight={s.is_primary ? "fill" : "regular"} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setHistoryContactId(rowId)}
                          className="font-display font-bold text-base hover:text-primary text-left"
                          data-testid={`contact-open-history-${rowId}`}
                        >
                          {s.name}
                        </button>
                        {s.title && <span className="text-xs text-muted-foreground">· {s.title}</span>}
                        {s.is_primary && (
                          <span className="text-[0.625rem] font-mono uppercase tracking-widest text-primary border border-primary/40 px-1.5 py-0.5" data-testid={`contact-primary-badge-${rowId}`}>
                            Primary
                          </span>
                        )}
                        {s.last_contacted_at && (
                          <span
                            className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground border border-border px-1.5 py-0.5"
                            title={`Last contacted ${s.last_contacted_at}`}
                            data-testid={`contact-last-contacted-${rowId}`}
                          >
                            {fmtRelative(s.last_contacted_at)}
                          </span>
                        )}
                        {isDecayed(s) && (
                          <button
                            type="button"
                            onClick={(ev) => { ev.stopPropagation(); setNudgeContact(s); }}
                            className="inline-flex items-center gap-1 text-[0.625rem] font-mono uppercase tracking-widest text-amber-600 dark:text-amber-400 border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 px-1.5 py-0.5 cursor-pointer transition-colors"
                            title={s.last_contacted_at ? `No touch in ${DECAY_DAYS}+ days — click to draft a nudge` : "High-influence contact — never touched"}
                            data-testid={`contact-decay-${rowId}`}
                          >
                            Reach out
                          </button>
                        )}
                      </div>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="text-[0.625rem] font-mono uppercase tracking-widest border border-border px-2 py-0.5 text-foreground/80" data-testid={`contact-role-${rowId}`}>
                          {ROLE_LABEL[s.role] || ROLE_LABEL.other}
                        </span>
                        <span className={`text-[0.625rem] font-mono uppercase tracking-widest border px-2 py-0.5 ${postureCls}`} data-testid={`contact-posture-${rowId}`}>
                          {POSTURE_LABEL[s.posture] || POSTURE_LABEL.unknown}
                        </span>
                        <span className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                          Influence · {s.influence || "medium"}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center gap-4 flex-wrap text-xs text-muted-foreground">
                        {s.email && (
                          <a href={`mailto:${s.email}`} className="inline-flex items-center gap-1 hover:text-primary" data-testid={`contact-email-${rowId}`}>
                            <EnvelopeSimple size={11} /> {s.email}
                          </a>
                        )}
                        {s.phone && (
                          <a href={`tel:${s.phone.replace(/\s+/g, "")}`} className="inline-flex items-center gap-1 hover:text-primary" data-testid={`contact-phone-${rowId}`}>
                            <PhoneIcon size={11} /> {s.phone}
                          </a>
                        )}
                        {s.linkedin_url && (
                          <a href={s.linkedin_url.startsWith("http") ? s.linkedin_url : `https://${s.linkedin_url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-primary" data-testid={`contact-linkedin-${rowId}`}>
                            <LinkedinLogo size={11} /> LinkedIn
                          </a>
                        )}
                      </div>
                      {s.notes && (
                        <p className="mt-2 text-xs text-muted-foreground italic border-l-2 border-border pl-3">{s.notes}</p>
                      )}
                    </div>
                    <div className="shrink-0 flex items-center gap-2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                      <button type="button" onClick={() => openEditContact(s)} className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1" data-testid={`contact-edit-${rowId}`}>
                        <PencilSimple size={11} /> Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => { setMergeSourceId(rowId); setMergeTargetId(null); }}
                        className="text-xs text-muted-foreground hover:text-primary"
                        title="Merge this contact into another"
                        data-testid={`contact-merge-open-${rowId}`}
                      >
                        Merge
                      </button>
                      <button type="button" onClick={() => removeContact(rowId)} className="text-xs text-muted-foreground hover:text-destructive" data-testid={`contact-remove-${rowId}`}>
                        Remove
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <ContactHistoryDrawer
        clientId={cid}
        contactId={historyContactId}
        open={!!historyContactId}
        onOpenChange={(v) => !v && setHistoryContactId(null)}
      />
      <ContactsCsvImportDialog
        clientId={cid}
        open={csvOpen}
        onOpenChange={setCsvOpen}
        onImported={(c) => c && onClientUpdated?.(c)}
        existingContacts={stakeholders}
      />

      {/* Iter 85.5 · Contact Merge dialog — a partner clicks Merge on
          the source row, then picks a target from every other contact
          on the client. Field union runs server-side (target wins ties,
          source fills blanks) plus Note/Task contact_ids reassign. */}
      <Dialog open={!!mergeSourceId} onOpenChange={(v) => { if (!v) { setMergeSourceId(null); setMergeTargetId(null); } }}>
        <DialogContent className="rounded-none max-w-md" data-testid="contact-merge-dialog">
          <DialogHeader>
            <DialogTitle>Merge contacts</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              {(() => {
                const src = stakeholders.find((s) => s.id === mergeSourceId);
                return src ? `Absorb "${src.name}" into another contact. History and touch cadence consolidate onto the target.` : "";
              })()}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Merge into</Label>
              <Select value={mergeTargetId || ""} onValueChange={(v) => setMergeTargetId(v)}>
                <SelectTrigger className="rounded-none bg-secondary/40 mt-1" data-testid="contact-merge-target"><SelectValue placeholder="Pick the keeper contact" /></SelectTrigger>
                <SelectContent>
                  {stakeholders.filter((s) => s.id !== mergeSourceId).map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`contact-merge-target-option-${s.id}`}>
                      {s.name}{s.title ? ` · ${s.title}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
              Every note and task tagged with the source contact will be redirected to the keeper. This is irreversible.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => { setMergeSourceId(null); setMergeTargetId(null); }}>Cancel</Button>
            <Button
              onClick={doMerge}
              disabled={!mergeTargetId}
              className="rounded-none bg-primary text-primary-foreground"
              data-testid="contact-merge-confirm"
            >
              Merge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 85.6 · Nudge Composer — Claude drafts a warm opener for a
          decayed contact. Contact object carries name/email so mailto:
          works without another lookup. */}
      <NudgeComposerDialog
        client={client}
        contact={nudgeContact}
        open={!!nudgeContact}
        onOpenChange={(v) => !v && setNudgeContact(null)}
      />
    </div>
  );
}
